import { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import HomePage from "./pages/HomePage";
import RoleLandingPage from "./pages/RoleLandingPage";
import HostCoHostPage from "./pages/HostCoHostPage";
import PlanSelectionPage from "./pages/PlanSelectionPage";
import CreateEventPage from "./pages/CreateEventPage";
import EventDetailsPage from "./pages/EventDetailsPage";
import EventPhotosPage from "./pages/EventPhotosPage";
import HostDashboard from "./pages/HostDashboard";
import MyRSVPsPage from "./pages/MyRSVPsPage";
import AdminDashboard from "./pages/AdminDashboardNew";
import JoinEventPage from "./pages/JoinEventPage";
import ApprovalsPage from "./pages/ApprovalsPage";
import ApprovalsDashboard from "./pages/ApprovalsDashboard";
import UnifiedApprovalsDashboard from "./pages/UnifiedApprovalsDashboard";
import PlanPurchaseHistory from "./pages/PlanPurchaseHistory";
import WishlistPage from "./pages/WishlistPage";
import WishlistHubPage from "./pages/WishlistHubPage";
import MyWishlistsPage from "./pages/MyWishlistsPage";
import ViewOthersWishlistPage from "./pages/ViewOthersWishlistPage";
import EditWishlistPage from "./pages/EditWishlistPage";
import SharedWishlistViewPage from "./pages/SharedWishlistViewPage";
import SavedEventsPage from "./pages/SavedEventsPage";
import PaymentPage from "./pages/PaymentPage";
import BottomNavigation from "./components/BottomNavigation";
import NotificationSettings from "./components/NotificationSettings";
import { trackPageView, trackPWALaunch, trackPWAInstall, trackLogin, setUserProperties } from "./utils/analytics";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Configure axios to include credentials (cookies) with all requests
axios.defaults.withCredentials = true;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Prevent double processing in React StrictMode
    if (hasProcessedRef.current) return;
    hasProcessedRef.current = true;

    // Track PWA launch
    trackPWALaunch();

    // Listen for PWA install
    window.addEventListener('appinstalled', () => {
      trackPWAInstall();
    });

    // Initialize auth
    initAuth();
  }, []);

  const initAuth = async () => {
    // Check for session_id in URL hash (OAuth callback)
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');
      
      if (sessionId) {
        // Clear the URL hash immediately
        window.history.replaceState(null, '', window.location.pathname);
        
        // Exchange session_id for token
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
          
          if (response.data.success) {
            setUser(response.data.user);
            setToken(response.data.session_token);
            trackLogin('google');
            if (response.data.user?.id) {
              setUserProperties(response.data.user.id, {
                user_email: response.data.user.email,
                user_name: response.data.user.name
              });
            }
          }
        } catch (error) {
          console.error('Auth error:', error);
        }
        setLoading(false);
        return;
      }
    }

    // No OAuth callback - check existing session
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setToken('authenticated');
      if (response.data?.id) {
        setUserProperties(response.data.id, {
          user_email: response.data.email,
          user_name: response.data.name
        });
      }
    } catch (error) {
      // No session - expected for new users
      setUser(null);
      setToken(null);
    }
    setLoading(false);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setToken(null);
    hasProcessedRef.current = false;
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-contribo-navy mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { token } = useAuth();
  return token ? children : <Navigate to="/" replace />;
};

// Page View Tracker Component
const PageViewTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location]);
  
  return null;
};

// Landing Page Router
const LandingPageRouter = () => {
  const { user } = useAuth();
  return user ? <RoleLandingPage /> : <HomePage />;
};

// Mobile Layout Wrapper
const MobileLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);

  const hideBottomNav = ['/admin', '/create-event', '/approvals/'].some(path => 
    location.pathname.startsWith(path)
  );

  const hideBackButton = location.pathname === '/';

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {user && (
        <div className="fixed top-0 left-0 right-0 bg-gradient-to-r from-contribo-navy to-contribo-gold text-white shadow-md z-40">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center space-x-2">
              {!hideBackButton && (
                <button
                  onClick={handleBack}
                  className="p-1.5 hover:bg-white hover:bg-opacity-20 rounded-md transition-colors mr-1"
                  aria-label="Go back"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div className="w-8 h-8 rounded-full bg-white text-contribo-navy flex items-center justify-center font-bold text-sm">
                {user.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold">{user.name || 'User'}</span>
                <span className="text-xs text-white text-opacity-80">{user.email}</span>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <button
                onClick={handleLogout}
                className="px-3 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-md text-xs font-medium transition-colors whitespace-nowrap"
              >
                Logout
              </button>
              <button
                onClick={() => navigate('/plan-history')}
                className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-md text-[10px] font-medium transition-colors flex items-center justify-center gap-1 whitespace-nowrap"
                title="Plan Purchase History"
              >
                <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span>Purchase History</span>
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className={user ? 'pt-16' : ''}>
        {children}
      </div>
      
      {user && !hideBottomNav && <BottomNavigation />}

      <NotificationSettings
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <PageViewTracker />
          <MobileLayout>
            <Routes>
              <Route path="/" element={<LandingPageRouter />} />
              <Route path="/host-cohost-dashboard" element={<ProtectedRoute><HostCoHostPage /></ProtectedRoute>} />
              <Route path="/join-event" element={<JoinEventPage />} />
              <Route path="/select-plan" element={<ProtectedRoute><PlanSelectionPage /></ProtectedRoute>} />
              <Route path="/create-event" element={<ProtectedRoute><CreateEventPage /></ProtectedRoute>} />
              <Route path="/event/:id" element={<EventDetailsPage />} />
              <Route path="/event/:eventId/photos" element={<ProtectedRoute><EventPhotosPage /></ProtectedRoute>} />
              <Route path="/wishlist" element={<ProtectedRoute><WishlistHubPage /></ProtectedRoute>} />
              <Route path="/wishlist/my-wishlists" element={<ProtectedRoute><MyWishlistsPage /></ProtectedRoute>} />
              <Route path="/wishlist/view-others" element={<ProtectedRoute><ViewOthersWishlistPage /></ProtectedRoute>} />
              <Route path="/wishlist/edit/:wishlistId" element={<ProtectedRoute><EditWishlistPage /></ProtectedRoute>} />
              <Route path="/wishlist/view/:shareCode" element={<ProtectedRoute><SharedWishlistViewPage /></ProtectedRoute>} />
              <Route path="/wishlist-legacy" element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
              <Route path="/saved-events" element={<ProtectedRoute><SavedEventsPage /></ProtectedRoute>} />
              <Route path="/payment" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
              <Route path="/approvals/:eventId" element={<ProtectedRoute><ApprovalsPage /></ProtectedRoute>} />
              <Route path="/approvals-dashboard" element={<ProtectedRoute><UnifiedApprovalsDashboard /></ProtectedRoute>} />
              <Route path="/approvals" element={<ProtectedRoute><UnifiedApprovalsDashboard /></ProtectedRoute>} />
              <Route path="/host-dashboard" element={<ProtectedRoute><HostDashboard /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><HostDashboard /></ProtectedRoute>} />
              <Route path="/my-rsvps" element={<ProtectedRoute><MyRSVPsPage /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><HostDashboard /></ProtectedRoute>} />
              <Route path="/plan-history" element={<ProtectedRoute><PlanPurchaseHistory /></ProtectedRoute>} />
            </Routes>
          </MobileLayout>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
export { API };
